/**
 * A day picker that looks the same in every browser and theme, unlike `<input type="date">`
 * (whose icon and popup follow the browser, not our tokens). Values are days, YYYY-MM-DD.
 */
import { CalendarIcon, X } from "lucide-react";
import { useState } from "react";
import { de, enGB } from "react-day-picker/locale";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { parseDay, toDay } from "@/lib/documents/document-list";
import { formatDate } from "@/lib/utils/format";
import { cn } from "@/lib/utils/cn";

type DatePickerProps = {
  id?: string;
  /** YYYY-MM-DD or "" */
  value: string;
  onChange: (value: string) => void;
  "aria-label"?: string;
  "data-testid"?: string;
};

export function DatePicker({ id, value, onChange, ...rest }: DatePickerProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const selected = value ? parseDay(value) : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          aria-label={rest["aria-label"]}
          data-testid={rest["data-testid"]}
          className={cn(
            "w-full justify-between bg-card font-normal num",
            !value && "text-muted-foreground",
          )}
        >
          {selected ? formatDate(selected) : t("dates.pick")}
          <CalendarIcon className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          className="bg-popover"
          locale={i18n.resolvedLanguage?.startsWith("de") ? de : enGB}
          selected={selected}
          defaultMonth={selected}
          weekStartsOn={1}
          onSelect={(date) => {
            onChange(date ? toDay(date) : "");
            setOpen(false);
          }}
        />
        {value && (
          <div className="border-t p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
            >
              <X /> {t("dates.clear")}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
